// =============================================================================
// features/sales/components/FormTextarea.tsx — Textarea canônico (DS §9.2:
// "Input / Select / Textarea" compartilham o mesmo tratamento visual).
//
// Não existe primitivo `Textarea` em components/ui/ ainda — este é o campo
// de observação/motivo usado pelo formulário de conclusão, reagendamento,
// cancelamento e `ScheduleAppointmentDialog`. Mesmos tokens do `Input`
// canônico (border-strong, inset shadow, foco azul, erro danger).
// =============================================================================

import * as React from 'react';

import { Label } from '../../../components/ui/Label';
import { cn } from '../../../lib/cn';

interface FormTextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  id: string;
  label?: string | undefined;
  error?: string | undefined;
  hint?: string | undefined;
  wrapperClassName?: string | undefined;
}

export const FormTextarea = React.forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  function FormTextarea(
    { id, label, error, hint, required, className, wrapperClassName, rows = 3, ...props },
    ref,
  ) {
    const hasError = Boolean(error);

    return (
      <div className={cn('flex flex-col gap-2', wrapperClassName)}>
        {label && (
          <Label htmlFor={id} {...(required === true ? { required: true } : {})}>
            {label}
          </Label>
        )}

        <textarea
          ref={ref}
          id={id}
          rows={rows}
          aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
          aria-invalid={hasError || undefined}
          required={required}
          className={cn(
            'w-full font-sans text-sm font-medium text-ink resize-y',
            'bg-surface-1 rounded-sm px-[14px] py-[11px]',
            'border border-border-strong',
            'shadow-[inset_0_1px_2px_var(--border-inner-dark)]',
            'transition-[border-color,box-shadow,background] duration-fast ease',
            'placeholder:text-ink-4',
            'hover:border-ink-3 hover:bg-surface-hover',
            'focus:outline-none focus:border-azul',
            'focus:shadow-[0_0_0_3px_rgba(27,58,140,0.15),inset_0_1px_2px_var(--border-inner-dark)]',
            'focus:bg-surface-1',
            hasError && [
              'border-danger',
              'focus:border-danger',
              'focus:shadow-[0_0_0_3px_rgba(200,52,31,0.15),inset_0_1px_2px_var(--border-inner-dark)]',
            ],
            'disabled:opacity-50 disabled:cursor-not-allowed',
            className,
          )}
          {...props}
        />

        {hasError ? (
          <span id={`${id}-error`} role="alert" className="text-xs text-danger">
            {error}
          </span>
        ) : hint ? (
          <span id={`${id}-hint`} className="text-xs text-ink-3">
            {hint}
          </span>
        ) : null}
      </div>
    );
  },
);
